import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Table from '@mui/material/Table';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import { useQuery } from 'react-query';
import { getSubaccountLevelPage } from '../../../data/apiCalls';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import useSubaccountLevelPageType from '../../../hooks/useSubaccountLevelPageType';
import CustomLoading from '../CustomLoading';

const PermissionsModal = ({ open, onClose }) => {
	const style = {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		maxWidth: '90vh',
		minWidth: 800,
		maxHeight: '90vh', // Limit height to 90% of viewport
		bgcolor: 'background.paper',
		boxShadow: 24,
		p: 0,
		outline: 'none',
		borderRadius: 1,
		display: 'flex',
		flexDirection: 'column',
	};

	const { providerUserType } = useSessionContext();
	const pageType = useSubaccountLevelPageType();

	const { data, isLoading, isError } = useQuery(
		['SubaccountPermissions', providerUserType],
		() => getSubaccountLevelPage(pageType),
		{
			staleTime: Infinity,
			enabled: open,
			retry: false,
		}
	);

	const { page } = data?.data?.data || {};

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby='permissions-modal-title'
			aria-describedby='permissions-modal-description'
		>
			<Box sx={style}>
				{/* Fixed Header */}
				<Typography
					sx={{ py: 2, px: 2 }}
					variant='h6'
					component='h1'
					id='permissions-modal-title'
				>
					ASHLink Permissions
				</Typography>

				{isLoading && <CustomLoading />}

				{isError && (
					<Box sx={{ p: 2 }}>
						<Alert severity='error'>
							{`"We're sorry, something went wrong.  Please try again later.`}
						</Alert>
					</Box>
				)}
				{/* Scrollable Content Area */}
				{!isLoading && !isError && (
					<Box
						sx={{
							flex: 1,
							overflow: 'auto',
							minHeight: 0, // Important for flex child to be scrollable
						}}
					>
						<Table
							stickyHeader
							sx={{
								'& .MuiTableCell-root': {
									textAlign: 'left',
									padding: '2px 16px',
									height: 'auto', // Let height adjust to content
								},
								// Style sticky header to match theme colors
								'& .MuiTableHead-root .MuiTableCell-head': {
									backgroundColor: 'primary.dark',
									color: 'primary.contrastText',
									position: 'sticky',
									top: 0,
									zIndex: 1,
								},
								// Remove row separators only in body rows (not header)
								'& .MuiTableBody-root .MuiTableCell-root': {
									borderBottom: 'none',
								},
								// Disable hovering color only for data rows (not section headers)
								'& .MuiTableBody-root .MuiTableRow-root:not([data-section-header]):hover, & .MuiTableBody-root .MuiTableRow-root:not([data-section-header]):hover .MuiTableCell-root':
									{
										backgroundColor: 'inherit !important',
									},
								// Prevent alternating row colors only in body rows (not header)
								'& .MuiTableBody-root .MuiTableRow-root': {
									backgroundColor: 'transparent !important',
								},
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell></TableCell>
									<TableCell>Primary</TableCell>
									<TableCell>Level 1</TableCell>
									<TableCell>Level 2</TableCell>
									<TableCell>Level 3</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{page?.sections?.map(
									(section, sectionIndex) => (
										<React.Fragment
											key={
												section.displayName ||
												sectionIndex
											}
										>
											<TableRow
												data-section-header
												sx={{
													'& .MuiTableCell-root': {
														paddingY: '10px',
													},
												}}
											>
												<TableCell
													sx={{
														backgroundColor:
															'#e0e0e0',
														fontWeight: 'bold',
													}}
													colSpan={5}
												>
													{section.displayName}
												</TableCell>
											</TableRow>

											{section?.permissions?.map(
												(
													permission,
													permissionIndex
												) => (
													<TableRow
														key={`${section.displayName}-${permission.displayName}-${permissionIndex}`}
													>
														<TableCell>
															{
																permission.displayName
															}
														</TableCell>
														<TableCell>
															<Checkbox
																checked={
																	permission.primary
																}
																disabled={true}
															/>
														</TableCell>
														<TableCell>
															<Checkbox
																checked={
																	permission.level1
																}
																disabled={true}
															/>
														</TableCell>
														<TableCell>
															<Checkbox
																checked={
																	permission.level2
																}
																disabled={true}
															/>
														</TableCell>
														<TableCell>
															<Checkbox
																checked={
																	permission.level3
																}
																disabled={true}
															/>
														</TableCell>
													</TableRow>
												)
											)}
										</React.Fragment>
									)
								)}
							</TableBody>
						</Table>
					</Box>
				)}
				{/* Fixed Footer */}
				<Box
					sx={{
						p: 2,
						borderTop:
							!isLoading && !isError
								? '1px solid #e0e0e0'
								: 'none',
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					<Button onClick={onClose} variant='contained'>
						Close
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default PermissionsModal;

PermissionsModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};
